namespace Nbs.Xlr8PageTemplate.Theme {
  let nonce: string | undefined;
  let currentTheme: Theme | undefined;

  export function setTheme(theme?: Theme) {
    currentTheme = theme ? theme : getDefaultTheme(window.location.hostname);
    Favicon.setFavicon(currentTheme.faviconUrl);
    Utilities.replaceChildren(getThemeStyleSheetElement(), [document.createTextNode(getThemeStyleCss())]);
    Header.renderLogo();
    setBrowserUiColor();
  }

  export function setNonce(value?: string) {
    nonce = value;
  }

  export function getCurrentTheme() {
    return currentTheme;
  }

  export function getDefaultTheme(host: string) {
    const hostname = host.toLowerCase();
    if (hostname.indexOf('factsmgt.com') > -1) {
      return factsDefaultTheme;
    }
    return nccDefaultTheme;
  }

  function getThemeStyleSheetElement() {
    const id = 'xpt-theme-color';
    let styleElement = document.getElementById(id);
    if (styleElement) {
      if (nonce) {
        styleElement.setAttribute('nonce', nonce);
      }
    } else {
      styleElement = Utilities.createElement({ tag: 'style', attributes: { id, nonce, type: 'text/css' } });
      document.head.insertAdjacentElement('beforeend', styleElement);
      styleElement = document.getElementById(id);
    }
    return (styleElement as HTMLStyleElement);
  }

  function getMainColor(propertyName: keyof ColorSwatch, palette: Palette | undefined, fallbackTheme: Theme) {
    let color = '';
    if (palette && palette.main) {
      color = getSwatchColor(propertyName, palette.main);
    }
    if (!color && fallbackTheme && fallbackTheme.primary) {
      color = getSwatchColor(propertyName, fallbackTheme.primary.main as ColorSwatch);
    }
    return color;
  }

  function getContrastColor(propertyName: keyof ColorSwatch, palette: Palette | undefined, fallbackTheme: Theme) {
    let color = '';
    if (palette && palette.contrast) {
      color = getSwatchColor(propertyName, palette.contrast);
    }
    if (!color && fallbackTheme && fallbackTheme.primary) {
      color = getSwatchColor(propertyName, fallbackTheme.primary.contrast as ColorSwatch);
    }
    return color;
  }

  function calculateContrastColor(color: string) {
    const whiteCutoffPoint = 150;
    const radix = 16;

    // 6 digit color format: #1f2f3f
    const rgb = /^#([\da-fA-F]{2})([\da-fA-F]{2})([\da-fA-F]{2})$/.exec(color);

    if (rgb) {
      for (const item of rgb) {
        const value = parseInt(item, radix);
        if (value > whiteCutoffPoint) {
          return '#000000';
        }
      }
      return '#FFFFFF';
    }

    return undefined;
  }

  function getSwatchColor(propertyName: keyof ColorSwatch, swatch: ColorSwatch) {
    return swatch[propertyName] ?? '';
  }

  function getThemeStyleCss() {
    const fallbackTheme = getDefaultTheme(window.location.hostname);

    const palette = getPalette();

    const main50 = getMainColor(m50, palette, fallbackTheme);
    const main500 = getMainColor(m500, palette, fallbackTheme);

    const contrast50 = getContrastColor(m50, palette, fallbackTheme);
    const contrast500 = getContrastColor(m500, palette, fallbackTheme);

    const themeStyles: string[] = [];
    themeStyles.push(`nbs-header .xpt-header-container {background-color: ${main500};color: ${contrast500};border-bottom-color: ${main500};}`);
    themeStyles.push(`nbs-header .xpt-header-container a:link {color: ${contrast500};}`);
    themeStyles.push(`nbs-header .xpt-header-container a:visited {color: ${contrast500};}`);
    themeStyles.push(`nbs-header .xpt-header-container a:active {color: ${contrast500};}`);
    themeStyles.push(`nbs-header .xpt-header-container a:hover {color: ${contrast500};}`);

    themeStyles.push(`nbs-menu nav .xpt-menu-link-item.xpt-active-menu-item > a {background-color: ${main50}; color: ${contrast50};}`);
    themeStyles.push(`nbs-menu nav .xpt-flyout-selected {background-color: ${main50}; color: ${contrast50};}`);
    themeStyles.push(`nbs-menu nav>ul>li.xpt-expander-menu-item>ul>li.xpt-expander-menu-item>ul>li.xpt-menu-link-item.xpt-active-menu-item>a>div.xpt-menu-link-icon {border-right-color: ${main500};}`);
    themeStyles.push(`nbs-menu nav>ul>li.xpt-expander-menu-item>ul>li.xpt-expander-menu-item>ul>li.xpt-menu-link-item.xpt-active-menu-item>a>div.xpt-menu-link-label {background-color: ${main50}; color: ${contrast50};}`);
    themeStyles.push(
      `@media screen and (max-width: 768px) { .xpt-header-menu-button, .xpt-header-link button { color: ${contrast500}; } }`
    );

    if (currentTheme && currentTheme.logoBackgroundHexColor) {
      const logoBackgroundHexColor = currentTheme.logoBackgroundHexColor;
      const logoForegroundHexColor = calculateContrastColor(logoBackgroundHexColor);
      if (logoForegroundHexColor) {
        themeStyles.push(`nbs-header .xpt-header-container {background-color: ${logoBackgroundHexColor}; color: ${logoForegroundHexColor};border-bottom-color: ${main500};}`);
        themeStyles.push(`nbs-header .xpt-header-container a:link {color: ${logoForegroundHexColor};}`);
        themeStyles.push(`nbs-header .xpt-header-container a:visited {color: ${logoForegroundHexColor};}`);
        themeStyles.push(`nbs-header .xpt-header-container a:active {color: ${logoForegroundHexColor};}`);
        themeStyles.push(`nbs-header .xpt-header-container a:hover {color: ${logoForegroundHexColor};}`);
        themeStyles.push(
          `@media screen and (max-width: 768px) { .xpt-header-menu-button, .xpt-header-link button { color: ${logoForegroundHexColor}; } }`
        );
      }
    }

    return themeStyles.join('');
  }

  // Set the browser's toolbar color for browsers that support it, like Chrome on Android and Vivaldi
  function setBrowserUiColor() {
    const fallbackTheme = getDefaultTheme(window.location.hostname);
    const palette = getPalette();
    const color = getMainColor(m900, palette, fallbackTheme);

    // The first item is the standard name
    // The second item is the iOS name
    for (const metaName of ['theme-color', 'apple-mobile-web-app-status-bar-style']) {
      const existingThemeColor = document.querySelector(`meta[name="${metaName}"]`);
      if (existingThemeColor) {
        existingThemeColor.setAttribute('content', color);
      } else {
        const themeColor = Utilities.createElement({
          tag: 'meta',
          attributes: {
            name: metaName,
            content: color,
          },
        });
        document.head.insertAdjacentElement('beforeend', themeColor);
      }
    }
  }

  function getPalette() {
    if (currentTheme) {
      if (currentTheme.header) {
        return currentTheme.header;
      }

      if (currentTheme.primary) {
        return currentTheme.primary;
      }
    }

    return undefined;
  }
}
